import { useTranslation } from "react-i18next";
import { Eye, Hand, Loader2, UserCheck, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { DataTableStates } from "@/shared/ui/DataTableStates";
import type { CsSession } from "../../api/aiKefuService";
import { SESSION_STATUS_KEYS, channelLabel } from "./constants";
import { canPickup } from "../../model/kefuRealtime";

type Props = {
  sessions: CsSession[];
  isLoading: boolean;
  takeoverPendingId?: string;
  closePendingId?: string;
  onSelectSession: (sessionId: string) => void;
  /**
   * 不传 = 不显示「接管 / 接手」(1002:客服舱停用时只读 + 人工处理,接管属 AI 口之外的坐席调度,仍 403)。
   * 带上渠道:企微弹「选择接待人员」,网页 / H5 直接接管(1010 一期补完 B2)。
   */
  onTakeover?: (sessionId: string, channel: string) => void;
  onCloseSession: (sessionId: string) => void;
};

export function SessionsTable({
  sessions,
  isLoading,
  takeoverPendingId,
  closePendingId,
  onSelectSession,
  onTakeover,
  onCloseSession,
}: Props) {
  const { t } = useTranslation("customer-service");
  // audit-0713 C3b:接管/关闭后端是 MANAGER+,STAFF 此前可见可点必 403 —— 前端对齐后端门
  const { user } = useAuth();
  const canOperate = isTenantManagerPlus(user?.role);
  return (
    <Card className="shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("sessionsTable.headerKfName")}</TableHead>
            <TableHead>{t("sessionsTable.headerVisitor")}</TableHead>
            <TableHead>{t("sessionsTable.headerSessionId")}</TableHead>
            <TableHead>{t("sessionsTable.headerSeatUser")}</TableHead>
            <TableHead>{t("sessionsTable.headerChannel")}</TableHead>
            <TableHead>{t("sessionsTable.headerStatus")}</TableHead>
            <TableHead>{t("sessionsTable.headerCreatedAt")}</TableHead>
            <TableHead>{t("sessionsTable.headerActions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* 三态收口(G4):加载/空态统一(错误在父层) */}
          <DataTableStates
            colSpan={8}
            isLoading={isLoading}
            isEmpty={sessions.length === 0}
            emptyMessage={t("sessionsTable.empty")}
          />
          {!isLoading &&
            sessions.map((s, i) => {
              const statusEntry = SESSION_STATUS_KEYS[s.status];
              const statusInfo = statusEntry
                ? { label: t(statusEntry.labelKey), type: statusEntry.type }
                : { label: s.status, type: "info" as const };
              const isPending = takeoverPendingId === s.sessionId || closePendingId === s.sessionId;
              return (
                <TableRow
                  key={s.id}
                  className="fx-enter"
                  style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
                >
                  <TableCell className="text-sm font-medium">{s.kfName || "-"}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-2">
                      {s.customer?.avatar ? (
                        <img
                          src={s.customer.avatar}
                          alt={s.customer.nickname ?? t("sessionsTable.avatarAlt")}
                          className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                        />
                      ) : null}
                      <span className="text-muted-foreground truncate max-w-[12rem]">
                        {s.customer?.nickname || s.visitorName || "-"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium text-primary font-mono text-xs">
                    {s.sessionId}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {s.seatUserName ?? (s.seatUserId ? `#${s.seatUserId}` : "—")}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" data-testid="session-channel">
                      {channelLabel(t, s.channel, s.entryName)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <StatusBadge type={statusInfo.type}>{statusInfo.label}</StatusBadge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {s.createdAt
                      ? new Date(s.createdAt).toLocaleString("zh-CN", { hour12: false })
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {/* 1010 一期补完 B2:人工处理中、还没人接的网页 / H5 会话 →「接手」(同一个接管接口) */}
                      {onTakeover && canOperate && canPickup(s) && (
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          disabled={isPending}
                          data-testid="session-pickup"
                          onClick={() => onTakeover(s.sessionId, s.channel)}
                        >
                          {isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Hand className="h-3 w-3 mr-1" />
                          )}
                          {t("sessionsTable.actionPickup")}
                        </Button>
                      )}
                      {onTakeover &&
                        canOperate &&
                        s.status !== "closed" &&
                        s.status !== "human_handling" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            disabled={isPending}
                            data-testid="session-takeover"
                            onClick={() => onTakeover(s.sessionId, s.channel)}
                          >
                            {isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <UserCheck className="h-3 w-3 mr-1" />
                            )}
                            {t("sessionsTable.actionTakeover")}
                          </Button>
                        )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => onSelectSession(s.sessionId)}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        {t("sessionsTable.actionDetail")}
                      </Button>
                      {canOperate && s.status !== "closed" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:text-destructive"
                          disabled={isPending}
                          onClick={() => onCloseSession(s.sessionId)}
                        >
                          <X className="h-3 w-3 mr-1" />
                          {t("sessionsTable.actionClose")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </Card>
  );
}
